/**
 * VehicleGlyphs.js — line icons for the six canonical vehicle types
 *
 * Why these are hand-drawn rather than pulled from the icon library: the
 * library has exactly two truck-shaped glyphs and no flatbed at all, so the
 * six types were being covered by four icons plus a shipping-container box
 * standing in for a flatbed — which read as a parcel, not a vehicle. Two of
 * the six were also sharing one glyph.
 *
 * Each type gets the silhouette detail that actually distinguishes it in the
 * yard:
 *   Heavy Truck   long trailer, twin rear axle
 *   Medium Truck  short rigid box, single rear axle
 *   Cargo Van     one continuous body, sloped screen, no separate box
 *   Flatbed       headboard behind the cab and a long empty deck
 *   Pick Up       cab plus a low open bed
 *   Tricycle      three wheels, cargo box over the rear pair
 *
 * Drawn on the same 24×24 grid, stroke-only, round caps and joins, so they
 * sit correctly next to the library icons used everywhere else. The props
 * match that library's API (size / strokeWidth / color / className) so these
 * drop in wherever one of its icons was.
 */

import React from 'react';

function Glyph({ size = 24, strokeWidth = 1.5, color = 'currentColor', className, children, ...rest }) {
  return (
    <svg
      width={size} height={size} viewBox="0 0 24 24"
      fill="none" stroke={color} strokeWidth={strokeWidth}
      strokeLinecap="round" strokeLinejoin="round"
      className={className} aria-hidden="true" {...rest}
    >
      {children}
    </svg>
  );
}

/* All six face the same way — cab to the right, load to the left. Mixed
   direction was the single thing that made the set look assembled rather
   than drawn: two vehicles pointing the other way read as broken before you
   could even name what was wrong. */

/* Tractor plus a tall box trailer, with a gap at the hitch. The twin rear
   axle and the extra body height are what say "heavy" next to the rigid
   truck beside it. */
export const HeavyTruckGlyph = (p) => (
  <Glyph {...p}>
    <path d="M1.5 15V5h11.5v10" />
    <path d="M14 15V9.5h3.6l3 3V15" />
    <circle cx="5" cy="17" r="1.8" />
    <circle cx="8.8" cy="17" r="1.8" />
    <circle cx="17.3" cy="17" r="1.8" />
  </Glyph>
);

/* Rigid box truck — body and cab are one unit, one axle at the back. Shorter
   and lower than the heavy, and no hitch gap. */
export const MediumTruckGlyph = (p) => (
  <Glyph {...p}>
    <path d="M2.5 15V8h9v7" />
    <path d="M11.5 15v-4h4l3 3V15" />
    <circle cx="6" cy="17" r="1.8" />
    <circle cx="15.5" cy="17" r="1.8" />
  </Glyph>
);

/* One unbroken shell, roof to raked screen to short bonnet. The absence of a
   step between cab and load space is the whole difference from a box truck,
   so nothing is drawn inside it. */
export const CargoVanGlyph = (p) => (
  <Glyph {...p}>
    <path d="M2.5 15V7h11l4.5 4H20v4" />
    <circle cx="6.4" cy="17" r="1.8" />
    <circle cx="16.4" cy="17" r="1.8" />
  </Glyph>
);

/* The headboard standing proud of the cab roof, and a long empty deck ahead
   of it. Those two together are the flatbed — without the headboard it just
   reads as a truck missing its body. */
export const FlatbedGlyph = (p) => (
  <Glyph {...p}>
    <path d="M2.5 14h12" />
    <path d="M2.5 14v-2.8" />
    <path d="M14.5 14V6" />
    <path d="M14.5 8.5h3.6l2.4 3V14" />
    <circle cx="6" cy="16.4" r="1.7" />
    <circle cx="17.4" cy="16.4" r="1.7" />
  </Glyph>
);

/* The step down from cab roof to bed rail is the entire silhouette of a
   pickup, so the bed is drawn low and open and the cab is squared off. */
export const PickUpGlyph = (p) => (
  <Glyph {...p}>
    <path d="M2.5 15v-4h9" />
    <path d="M11.5 15V7.6h4.4l3.6 3.8V15" />
    <circle cx="6.4" cy="17" r="1.8" />
    <circle cx="16" cy="17" r="1.8" />
  </Glyph>
);

/* Three wheels, said plainly: the overlapping pair carries the cargo box,
   the single one in front carries the steering column. Drawing the column
   and bars rather than a bent frame line is what stops it reading as a
   truck with a crane on it. */
export const TricycleGlyph = (p) => (
  <Glyph {...p}>
    <path d="M2.5 12.5V7h8.5v5.5" />
    <path d="M11 12.2h7" />
    <path d="M18 12.4V8" />
    <path d="M16.4 8h3.2" />
    <circle cx="4.9" cy="16.6" r="2.1" />
    <circle cx="8.6" cy="16.6" r="2.1" />
    <circle cx="18" cy="16.6" r="2.1" />
  </Glyph>
);

/** Keyed to the canonical list in config/vehicleTypes.js. */
export const VEHICLE_GLYPHS = {
  'Heavy Truck':  HeavyTruckGlyph,
  'Medium Truck': MediumTruckGlyph,
  'Cargo Van':    CargoVanGlyph,
  'Flatbed':      FlatbedGlyph,
  'Pick Up':      PickUpGlyph,
  'Tricycle':     TricycleGlyph,
};

export const DEFAULT_VEHICLE_GLYPH = MediumTruckGlyph;
